<template>
  <b-modal
    v-b-modal.modal-lg
    ref="modal-dealsheet-imeiCode"
    hide-footer
    :title="titleImeiModel"
    id="modal-dealsheet-imeiCode"
    size="lg"
  >
    <b-table
      hover
      :items="imeiDataRows"
      :fields="fieldImeis"
      :per-page="10"
      :current-page="currentPageImei"
      show-empty
      bordered
    >
      <template #empty="">
        <h3 class="text-center">Không có dữ liệu!</h3>
      </template>
      <template #cell(index)="row">
        {{ row.item.index + 1 }}
      </template>
      <template #cell(sourceFromName)="row">
        <div class="d-flex flex-column">
          <span class="text-capitalize">{{ row.item.sourceFromName }}</span>
          <template v-if="getEntityLink(row.item).id">
            <span v-if="getEntityLink(row.item).link" class="mt-1">
              <span
                class="text-primary cursor-pointer font-weight-bold hover-link"
                title="Xem chi tiết"
                @click="linkToEntity(row.item)"
              >
                #{{ getEntityLink(row.item).id }}
              </span>
            </span>
            <span v-else class="mt-1 text-muted">#{{ getEntityLink(row.item).id }}</span>
          </template>
        </div>
      </template>
      <template #cell(date)="row">
        <ul class="list-unstyled mb-0">
          <li v-if="row.item.saleDate">
            <strong>Ngày bán:</strong>
            {{ convertToDateSlash(row.item.date, DATE_FORMAT) }}
          </li>

          <li v-if="row.item.importDate">
            <strong>Ngày nhập:</strong>
            {{ convertToDateSlash(row.item.importDate, DATE_FORMAT) }}
          </li>
          <li v-if="row.item.unitPrice">
            <strong>Giá nhập:</strong>
            {{ convertPrice(row.item.unitPrice) }}
          </li>
          <li v-if="row.item.revenue">
            <strong>Doanh thu:</strong>
            {{ convertPrice(row.item.revenue) }}
          </li>
        </ul>
      </template>
      <template #cell(activeDate)="row">
        <span v-if="row.item.activeDate">
          {{ convertToDateSlash(row.item.activeDate, DATE_FORMAT) }}
        </span>
      </template>
    </b-table>
    <b-row>
      <b-col fixed>
        <p class="mt-3 text-dark" style="font-weight: 500">
          Tổng số:
          {{ imeiDataRowsLength }}
        </p>
      </b-col>
      <b-pagination
        class="custom-pagination"
        v-show="imeiDataRowsLength > 10"
        v-model="currentPageImei"
        :total-rows="imeiDataRowsLength"
        :per-page="10"
        align="right"
      >
      </b-pagination>
    </b-row>
    <div class="d-flex justify-content-end mt-3">
      <b-button
        size="sm"
        class="mr-2"
        :disabled="!imeiDataRowsLength"
        @click="exportToExcel"
      >
      <i style="font-size: 1rem" class="flaticon-file-2"></i>
        <strong>Xuất Excel</strong>
      </b-button>
      <b-button
        style="width: 80px"
        variant="secondary"
        size="sm"
        @click="hideModal()"
      >
        <strong>Đóng</strong>
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import XLSX from 'xlsx';
import { convertToDateSlash, DATE_FORMAT } from '@/utils/date';
import { IMEI_TABLE_FIELDS } from '@/view/pages/dealsheets/utils/tableConfig';
import { formatImeiData } from '@/view/pages/dealsheets/utils/apiHelpers';
import { DEAL_SHEET_SOURCE_TYPE } from '../../../utils/enum';
import { removeAccents, convertPrice } from '@/utils/common';

export default {
  name: 'DealSheetImeiReportModal',
  data() {
    return {
      fieldImeis: IMEI_TABLE_FIELDS,
      currentPageImei: 1,
      imeiDataRows: [],
      titleImeiModel: 'Danh sách IMEI',
      dealSheetCode: '',
      productName: '',
      categoryName: '',
      DATE_FORMAT,
    };
  },
  methods: {
    convertPrice,
    convertToDateSlash,
    showModal(item, context = {}) {
      const { imeiDetails } = item;
      const imeiData = formatImeiData({ imeiDetails });
      this.titleImeiModel = imeiData.title;
      this.imeiDataRows = imeiData.rows;
      this.dealSheetCode = context.dealSheetCode ?? '';
      this.productName = item.productName ?? '';
      this.categoryName = item.categoryName ?? '';
      this.$refs[`modal-dealsheet-imeiCode`].show();
    },
    hideModal() {
      this.$refs[`modal-dealsheet-imeiCode`].hide();
    },
    getEntityLink(item) {
      if (!item) return { id: null, link: null };
      switch (item.sourceFrom) {
        case DEAL_SHEET_SOURCE_TYPE.BILL:
          return {
            id: item.billNumber,
            link: `/bills/update-bill?id=${item.billNumber}`,
          };
        case DEAL_SHEET_SOURCE_TYPE.WHOLESALE:
          return {
            id: item.wholesaleInvoiceNumber ?? item.billNumber,
            link: `/wholesale-invoice/update-wholesale-invoice?id=${item.wholesaleInvoiceNumber ?? item.billNumber}`,
          };
        case DEAL_SHEET_SOURCE_TYPE.STOCK_SLIP:
          return {
            id: item.stockSlipCode ?? item.stockSlipId,
            link: `/inventory-receipt/edit-inventory-receipt?id=${item.stockSlipId ?? item.stockSlipCode}`,
          };
        case DEAL_SHEET_SOURCE_TYPE.ACTIVE:
          return {
            id: item.imeiId,
            link: `/imeis/update-imei?id=${item.imeiId}`,
          };
        default:
          return {
            id: item.billNumber ?? item.stockSlipCode ?? item.imeiCode ?? '-',
            link: null,
          };
      }
    },
    linkToEntity(item) {
      const { link } = this.getEntityLink(item);
      if (link) {
        const route = this.$router.resolve(link);
        window.open(route.href, '_blank');
      }
    },
    exportToExcel() {
      if (!this.imeiDataRowsLength) return;
      const exportData = this.imeiDataRows.map((item) => {
        const entity = this.getEntityLink(item);
        return {
          STT: item.index + 1,
          IMEI: item.imeiCode || '',
          Nguồn: item.sourceFromName || '',
          'Mã/ID': entity.id || '',
          'Ngày bán': item.saleDate ||  '',
          'Ngày nhập': item.importDate || '',
          'Ngày kích hoạt': item.activeDate || '',
          'Giá nhập': item.unitPrice ?? '',
          'Doanh thu': item.revenue ?? '',
        };
      });
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, 'Danh sách IMEI');
      const dateStr = new Date().toISOString().slice(0, 10);
      const parts = [
        removeAccents(this.dealSheetCode),
        removeAccents(this.categoryName || this.productName),
        dateStr,
      ].filter(Boolean);
      const fileName = parts.join('-') || `danh-sach-imei-${dateStr}`;
      XLSX.writeFile(wb, `${fileName}.xlsx`);
    },
  },
  computed: {
    imeiDataRowsLength() {
      return this.imeiDataRows.length;
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .table {
    th,
    td {
      padding: 0.75rem;
      vertical-align: middle;
      border-top: 1px solid #ecf0f3;
    }
  }
}
</style>
